import { Toaster, TooltipProvider } from "@medusajs/ui"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { lazy, StrictMode, Suspense, useEffect } from "react"
import { createRoot } from "react-dom/client"
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom"
import { PageFallback } from "./components/page"
import { Shell } from "./components/shell"
import "./index.css"
import { AuthProvider, useAuth } from "./lib/auth"
import { activate, LocaleProvider, storedChoice } from "./lib/i18n"
import { useAccountLanguage } from "./lib/language"
import { hqApi } from "./lib/medusa"
import { persistQueries, restoreQueries } from "./lib/query-persist"
import { rolesQuery } from "./lib/roles"
import { LoginPage } from "./routes/login"
import { MorePage } from "./routes/more"
import { OrderPage } from "./routes/order"
import { OrdersPage } from "./routes/orders"
import { TodayPage } from "./routes/today"

// The chart library and the CSV reader are only needed on some screens, so each section loads when
// opened — and, once signed in, all of them are fetched quietly in the background, so a tap on a
// phone never waits for a section's code.
const load = {
  analytics: () => import("./routes/analytics"),
  imports: () => import("./routes/imports"),
  custom: () => import("./routes/custom"),
  money: () => import("./routes/money"),
  forecast: () => import("./routes/forecast"),
  buying: () => import("./routes/buying"),
  stock: () => import("./routes/stock"),
  stockAge: () => import("./routes/stock-age"),
  catalogCheck: () => import("./routes/catalog-check"),
  products: () => import("./routes/products"),
  costs: () => import("./routes/costs"),
  purchaseOrders: () => import("./routes/purchase-orders"),
  productImport: () => import("./routes/product-import"),
  parcels: () => import("./routes/parcels"),
  slips: () => import("./routes/slips"),
  payments: () => import("./routes/payments"),
  replies: () => import("./routes/replies"),
  discounts: () => import("./routes/discounts"),
  courier: () => import("./routes/courier"),
  suppliers: () => import("./routes/suppliers"),
  extras: () => import("./routes/extras"),
  shopOrder: () => import("./routes/shop-order"),
  customNew: () => import("./routes/custom-new"),
  customOrder: () => import("./routes/custom-order"),
  settings: () => import("./routes/settings"),
  booking: () => import("./routes/booking"),
  finder: () => import("./routes/finder"),
  finderLink: () => import("./routes/finder-link"),
  catalogue: () => import("./routes/catalogue"),
}

const AnalyticsPage = lazy(() => load.analytics().then((module) => ({ default: module.AnalyticsPage })))
const ImportsPage = lazy(() => load.imports().then((module) => ({ default: module.ImportsPage })))
const CustomPage = lazy(() => load.custom().then((module) => ({ default: module.CustomPage })))
const MoneyPage = lazy(() => load.money().then((module) => ({ default: module.MoneyPage })))
const ForecastPage = lazy(() => load.forecast().then((module) => ({ default: module.ForecastPage })))
const BuyingPage = lazy(() => load.buying().then((module) => ({ default: module.BuyingPage })))
const StockPage = lazy(() => load.stock().then((module) => ({ default: module.StockPage })))
const StockAgePage = lazy(() => load.stockAge().then((module) => ({ default: module.StockAgePage })))
const CatalogCheckPage = lazy(() => load.catalogCheck().then((module) => ({ default: module.CatalogCheckPage })))
const CataloguePage = lazy(() => load.catalogue().then((module) => ({ default: module.CataloguePage })))
const CatalogueQueuePage = lazy(() => load.catalogue().then((module) => ({ default: module.CatalogueQueuePage })))
const CatalogueCodePage = lazy(() => load.catalogue().then((module) => ({ default: module.CatalogueCodePage })))
const CatalogueCoveragePage = lazy(() => load.catalogue().then((module) => ({ default: module.CatalogueCoveragePage })))
const CatalogueMergesPage = lazy(() => load.catalogue().then((module) => ({ default: module.CatalogueMergesPage })))
const NewProductPage = lazy(() => load.products().then((module) => ({ default: module.NewProductPage })))
const EditProductPage = lazy(() => load.products().then((module) => ({ default: module.EditProductPage })))
const BringBackPage = lazy(() => load.products().then((module) => ({ default: module.BringBackPage })))
const CostsPage = lazy(() => load.costs().then((module) => ({ default: module.CostsPage })))
const PurchaseOrderPage = lazy(() => load.purchaseOrders().then((module) => ({ default: module.PurchaseOrderPage })))
const ProductImportPage = lazy(() => load.productImport().then((module) => ({ default: module.ProductImportPage })))
const ParcelsPage = lazy(() => load.parcels().then((module) => ({ default: module.ParcelsPage })))
const ShipmentPage = lazy(() => load.parcels().then((module) => ({ default: module.ShipmentPage })))
const SlipsPage = lazy(() => load.slips().then((module) => ({ default: module.SlipsPage })))
const PaymentsPage = lazy(() => load.payments().then((module) => ({ default: module.PaymentsPage })))
const RepliesPage = lazy(() => load.replies().then((module) => ({ default: module.RepliesPage })))
const DiscountsPage = lazy(() => load.discounts().then((module) => ({ default: module.DiscountsPage })))
const CourierPage = lazy(() => load.courier().then((module) => ({ default: module.CourierPage })))
const SuppliersPage = lazy(() => load.suppliers().then((module) => ({ default: module.SuppliersPage })))
const ExtrasPage = lazy(() => load.extras().then((module) => ({ default: module.ExtrasPage })))
const ShopOrderPage = lazy(() => load.shopOrder().then((module) => ({ default: module.ShopOrderPage })))
const CustomNewPage = lazy(() => load.customNew().then((module) => ({ default: module.CustomNewPage })))
const CustomOrderPage = lazy(() => load.customOrder().then((module) => ({ default: module.CustomOrderPage })))
const SettingsPage = lazy(() => load.settings().then((module) => ({ default: module.SettingsPage })))
const MessagesPage = lazy(() => import("./routes/messages").then((module) => ({ default: module.MessagesPage })))
const BookingOverviewPage = lazy(() => load.booking().then((module) => ({ default: module.BookingOverviewPage })))
const BookingDetailPage = lazy(() => load.booking().then((module) => ({ default: module.BookingDetailPage })))
const BookingDayPage = lazy(() => load.booking().then((module) => ({ default: module.BookingDayPage })))
const BookingSchedulePage = lazy(() => load.booking().then((module) => ({ default: module.BookingSchedulePage })))
const BookingAreasPage = lazy(() => load.booking().then((module) => ({ default: module.BookingAreasPage })))
const BookingSettingsPage = lazy(() => load.booking().then((module) => ({ default: module.BookingSettingsPage })))
const BookingNewPage = lazy(() => load.booking().then((module) => ({ default: module.BookingNewPage })))
const FinderPage = lazy(() => load.finder().then((module) => ({ default: module.FinderPage })))
const FinderSearchPage = lazy(() => load.finder().then((module) => ({ default: module.FinderSearchPage })))
const FinderHistoryPage = lazy(() => load.finder().then((module) => ({ default: module.FinderHistoryPage })))
const FinderSavedPage = lazy(() => load.finder().then((module) => ({ default: module.FinderSavedPage })))
const FinderAlbumPage = lazy(() => load.finder().then((module) => ({ default: module.FinderAlbumPage })))
const FinderSuppliersPage = lazy(() => load.finder().then((module) => ({ default: module.FinderSuppliersPage })))
const FinderSupplierPage = lazy(() => load.finder().then((module) => ({ default: module.FinderSupplierPage })))
const FinderSupplierAlbumsPage = lazy(() => load.finder().then((module) => ({ default: module.FinderSupplierAlbumsPage })))
const FinderLinkPage = lazy(() => load.finderLink().then((module) => ({ default: module.FinderLinkPage })))
const FinderShopPage = lazy(() => load.finderLink().then((module) => ({ default: module.FinderShopPage })))

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, refetchOnWindowFocus: true },
  },
})
// The last lists and reports are shown at once and refreshed behind them (lib/query-persist.ts).
restoreQueries(queryClient)
persistQueries(queryClient)
// The team's roles decide which Today a person sees: asked for at once, beside the sign-in check, instead
// of after it (a round trip saved on every cold start). Signed out, it simply fails and is asked again later.
void queryClient.prefetchQuery(rolesQuery)

type IdleWindow = Window & {
  requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number
  cancelIdleCallback?: (handle: number) => void
}

/** Fetches every section's code once the phone is idle, so later taps open at once. */
function usePrefetchSections(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return
    const run = () => {
      for (const fetchSection of Object.values(load)) void fetchSection().catch(() => undefined)
      // The three lists most tabs open with, so switching tab shows them without a wait.
      void queryClient.prefetchQuery({ queryKey: ["hq", "orders"], queryFn: hqApi.orders })
      void queryClient.prefetchQuery({ queryKey: ["hq", "custom"], queryFn: hqApi.customOrders })
      void queryClient.prefetchQuery({ queryKey: ["hq", "product-profit"], queryFn: () => hqApi.productProfit() })
    }
    const idleWindow = window as IdleWindow
    if (idleWindow.requestIdleCallback) {
      const handle = idleWindow.requestIdleCallback(run, { timeout: 4000 })
      return () => idleWindow.cancelIdleCallback?.(handle)
    }
    const timer = window.setTimeout(run, 2500)
    return () => window.clearTimeout(timer)
  }, [enabled])
}

function App() {
  const { user, loading } = useAuth()
  const location = useLocation()
  usePrefetchSections(!!user)
  useAccountLanguage(user)
  if (loading) {
    return null
  }
  if (!user) {
    return <LoginPage />
  }
  // Print pages carry no navigation, so only the page itself reaches the printer.
  if (location.pathname.startsWith("/print/")) {
    return (
      <Suspense fallback={null}>
        <Routes>
          <Route path="/print/slips" element={<SlipsPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    )
  }
  return (
    <Shell>
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/" element={<TodayPage />} />
          <Route path="/more" element={<MorePage />} />
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/orders/:id" element={<OrderPage />} />
          <Route path="/custom" element={<CustomPage />} />
          <Route path="/custom/new" element={<CustomNewPage />} />
          <Route path="/custom/:number" element={<CustomOrderPage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/costs" element={<CostsPage />} />
          <Route path="/stock" element={<StockPage />} />
          <Route path="/stock-age" element={<StockAgePage />} />
          <Route path="/catalog-check" element={<CatalogCheckPage />} />
          <Route path="/catalogue" element={<CataloguePage />} />
          <Route path="/catalogue/queue" element={<CatalogueQueuePage />} />
          <Route path="/catalogue/code/:code" element={<CatalogueCodePage />} />
          <Route path="/catalogue/coverage" element={<CatalogueCoveragePage />} />
          <Route path="/catalogue/merges" element={<CatalogueMergesPage />} />
          {/* Products and Stock are one page now (Stock); the old address still works. */}
          <Route path="/products" element={<Navigate to="/stock" replace />} />
          <Route path="/products/new" element={<NewProductPage />} />
          <Route path="/products/bring-back" element={<BringBackPage />} />
          <Route path="/products/import" element={<ProductImportPage />} />
          <Route path="/products/:id" element={<EditProductPage />} />
          <Route path="/booking" element={<BookingOverviewPage />} />
          <Route path="/booking/new" element={<BookingNewPage />} />
          <Route path="/booking/b/:id" element={<BookingDetailPage />} />
          <Route path="/booking/day/:date" element={<BookingDayPage />} />
          <Route path="/booking/schedule" element={<BookingSchedulePage />} />
          <Route path="/booking/areas" element={<BookingAreasPage />} />
          <Route path="/booking/settings" element={<BookingSettingsPage />} />
          <Route path="/parcels" element={<ParcelsPage />} />
          <Route path="/buying" element={<BuyingPage />} />
          <Route path="/buying/orders/:id" element={<PurchaseOrderPage />} />
          <Route path="/buying/parcels/:key" element={<ShipmentPage />} />
          <Route path="/payments" element={<PaymentsPage />} />
          <Route path="/replies" element={<RepliesPage />} />
          <Route path="/discounts" element={<DiscountsPage />} />
          <Route path="/courier" element={<CourierPage />} />
          <Route path="/suppliers" element={<SuppliersPage />} />
          <Route path="/finder" element={<FinderPage />} />
          <Route path="/finder/s/:id" element={<FinderSearchPage />} />
          <Route path="/finder/history" element={<FinderHistoryPage />} />
          <Route path="/finder/saved" element={<FinderSavedPage />} />
          <Route path="/finder/a/:id" element={<FinderAlbumPage />} />
          <Route path="/finder/suppliers" element={<FinderSuppliersPage />} />
          <Route path="/finder/suppliers/:id" element={<FinderSupplierPage />} />
          <Route path="/finder/suppliers/:id/albums" element={<FinderSupplierAlbumsPage />} />
          <Route path="/finder/link" element={<FinderLinkPage />} />
          <Route path="/finder/shop" element={<FinderShopPage />} />
          <Route path="/extras" element={<ExtrasPage />} />
          <Route path="/shop-order" element={<ShopOrderPage />} />
          <Route path="/money" element={<MoneyPage />} />
          <Route path="/forecast" element={<ForecastPage />} />
          <Route path="/import" element={<ImportsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/messages" element={<MessagesPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </Shell>
  )
}

const render = () =>
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        {/* Changing language remounts everything inside, in the new language (lib/i18n.tsx). */}
        <LocaleProvider>
          <TooltipProvider>
            <BrowserRouter>
              <AuthProvider>
                <App />
              </AuthProvider>
            </BrowserRouter>
          </TooltipProvider>
        </LocaleProvider>
        <Toaster />
      </QueryClientProvider>
    </StrictMode>
  )

// Greek is its own small file, fetched before the first paint by those who chose it, so the app
// never flashes up in English first. If it cannot load (offline), HQ starts in English.
const choice = storedChoice()
void (choice?.locale === "el" ? activate("el").catch(() => undefined) : Promise.resolve()).then(render)
